import {ChangeDetectionStrategy, Component, Injectable, signal} from '@angular/core';
import {BrukerId, BrukerModell, BrukerRessursClient, ElasticSearchTreff, GruppeAggreatModell, GruppeId, GruppeMedlemModell, GruppeMedlemRessursClient, GruppeModell, GruppeRessursClient, IndeksertEntitet, MedlemId} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';
import {AdministrasjonBrukerKomponent} from '../administrasjon-bruker/administrasjon-bruker';
import {AdministrasjonGruppeKomponent} from '../administrasjon-gruppe/administrasjon-gruppe';
import {AdministrasjonMedlemKomponent} from '../administrasjon-medlem/administrasjon-medlem';
import {AdministrasjonNavigasjonKomponent} from '../administrasjon-navigasjon/administrasjon-navigasjon';
import {AdministrasjonSokKomponent} from '../administrasjon-sok/administrasjon-sok';

@Component({
    selector: 'administrasjon-sok-side',
    templateUrl: 'administrasjon-sok-side.html',
    styleUrl: 'administrasjon-sok-side.scss',
    host: {'data-testid': 'administrasjon-sok-side'},
    imports: [AdministrasjonNavigasjonKomponent, AdministrasjonSokKomponent, AdministrasjonGruppeKomponent, AdministrasjonBrukerKomponent, AdministrasjonMedlemKomponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdministrasjonSokSideKomponent {
    static ajsSelector = 'administrasjonSokSide';

    readonly gruppe = signal<GruppeModell | null>(null);
    readonly gruppeaggregat = signal<GruppeAggreatModell | null>(null);
    readonly bruker = signal<BrukerModell | null>(null);
    readonly medlem = signal<GruppeMedlemModell | null>(null);
    private valgtTreffNummer = 0;

    constructor(private avhengigheter: AdministrasjonSokSideAvhengigheter) {
    }

    async vedNavigerTilTreff(treff: ElasticSearchTreff<unknown>): Promise<void> {
        const treffNummer = ++this.valgtTreffNummer;
        this.nullstill();

        switch (treff._type) {
            case IndeksertEntitet.medlem:
                this.oppdaterHvisValgt(treffNummer, this.medlem, await this.avhengigheter.hentMedlem(treff._id));
                return;
            case IndeksertEntitet.bruker:
                this.oppdaterHvisValgt(treffNummer, this.bruker, await this.avhengigheter.hentBruker(treff._id));
                return;
            case IndeksertEntitet.gruppe: {
                const [gruppe, gruppeaggregat] = await Promise.all([
                    this.avhengigheter.hentGruppe(treff._id),
                    this.avhengigheter.hentGruppeaggregat(treff._id)
                ]);
                if (treffNummer !== this.valgtTreffNummer) return;
                this.gruppe.set(gruppe);
                this.gruppeaggregat.set(gruppeaggregat);
                return;
            }
        }
    }

    private nullstill(): void {
        this.gruppe.set(null);
        this.gruppeaggregat.set(null);
        this.bruker.set(null);
        this.medlem.set(null);
    }

    private oppdaterHvisValgt<T>(treffNummer: number, verdi: {set(verdi: T): void}, nyVerdi: T): void {
        if (treffNummer === this.valgtTreffNummer) verdi.set(nyVerdi);
    }
}

@Injectable({providedIn: 'root'})
export class AdministrasjonSokSideAvhengigheter {
    constructor(private gruppeRessurs: GruppeRessursClient,
                private brukerRessurs: BrukerRessursClient,
                private medlemRessurs: GruppeMedlemRessursClient) {
    }

    hentGruppe(id: GruppeId): Promise<GruppeModell> {
        return this.gruppeRessurs.hent(id).somPromise();
    }

    hentGruppeaggregat(id: GruppeId): Promise<GruppeAggreatModell> {
        return this.gruppeRessurs.gruppeAggregater(id).somPromise();
    }

    hentBruker(id: BrukerId): Promise<BrukerModell> {
        return this.brukerRessurs.hentBruker(id).somPromise();
    }

    hentMedlem(id: MedlemId): Promise<GruppeMedlemModell> {
        return this.medlemRessurs.hent(id).somPromise();
    }
}
